/**
 * Card validation utilities — pure functions for card input UX.
 * Luhn check, brand detection, formatting, expiry validation.
 */

/** Luhn algorithm — validates card number checksum */
export function luhnCheck(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '')
  if (digits.length < 13 || digits.length > 19) return false

  let sum = 0
  let alternate = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i]!, 10)
    if (alternate) {
      n *= 2
      if (n > 9) n -= 9
    }
    sum += n
    alternate = !alternate
  }
  return sum % 10 === 0
}

/** Detect card brand from BIN (first 6 digits) */
export function detectCardBrand(cardNumber: string): 'visa' | 'mastercard' | 'amex' | 'unknown' {
  const digits = cardNumber.replace(/\D/g, '')
  if (!digits) return 'unknown'

  // AMEX: starts with 34 or 37
  if (/^3[47]/.test(digits)) return 'amex'

  // Visa: starts with 4
  if (/^4/.test(digits)) return 'visa'

  // Mastercard: starts with 51-55 or 2221-2720
  if (/^5[1-5]/.test(digits)) return 'mastercard'
  const first4 = parseInt(digits.slice(0, 4), 10)
  if (first4 >= 2221 && first4 <= 2720) return 'mastercard'

  return 'unknown'
}

/** Format card number with spaces: "4242424242424242" → "4242 4242 4242 4242" */
export function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 16)
  const groups: string[] = []
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4))
  }
  return groups.join(' ')
}

/** Format expiry: "1225" → "12/25" */
export function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

/** Parse formatted expiry "MM/YY" → { month, year } or null if invalid */
export function parseExpiry(formatted: string): { month: number; year: number } | null {
  const match = formatted.match(/^(\d{2})\/(\d{2})$/)
  if (!match) return null
  const month = parseInt(match[1]!, 10)
  const year = 2000 + parseInt(match[2]!, 10)
  if (month < 1 || month > 12) return null
  return { month, year }
}

/** Check if card is expired (month/year already passed) */
export function isExpired(month: number, year: number): boolean {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  if (year < currentYear) return true
  if (year === currentYear && month < currentMonth) return true
  return false
}

/** CVV length by brand (4 for AMEX, 3 for everything else) */
export function cvvLength(brand: 'visa' | 'mastercard' | 'amex' | 'unknown'): number {
  return brand === 'amex' ? 4 : 3
}
